import type { SubscriptionCheckoutMode } from "@/domains/subscription/billingHelpers";
import type { SubscriptionPlanId } from "@/domains/subscription/plans";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from "react";

export type SubscriptionCheckoutInitialStep = "plan" | "method";

export type OpenCheckoutOptions = {
    mode?: SubscriptionCheckoutMode;
    initialStep?: SubscriptionCheckoutInitialStep;
    nonDismissable?: boolean;
    showBasicOption?: boolean;
    /** 1.2 Assinatura conversion: plan step is Básico ↔ Pro only (no Gratuito / Clínica). */
    conversionCheckout?: boolean;
};

type SubscriptionCheckoutContextValue = {
    open: boolean;
    initialPlanId?: SubscriptionPlanId;
    mode: SubscriptionCheckoutMode;
    initialStep: SubscriptionCheckoutInitialStep;
    nonDismissable: boolean;
    showBasicOption: boolean;
    conversionCheckout: boolean;
    openCheckout: (planId?: SubscriptionPlanId, options?: OpenCheckoutOptions) => void;
    closeCheckout: () => void;
    /** Set while redirecting to profile address so pending checkout does not reopen on Assinatura. */
    skipPendingCheckoutAutoOpenRef: MutableRefObject<boolean>;
};

const SubscriptionCheckoutContext = createContext<SubscriptionCheckoutContextValue | null>(null);

export function SubscriptionCheckoutProvider({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false);
    const [initialPlanId, setInitialPlanId] = useState<SubscriptionPlanId | undefined>();
    const [mode, setMode] = useState<SubscriptionCheckoutMode>("checkout");
    const [initialStep, setInitialStep] = useState<SubscriptionCheckoutInitialStep>("plan");
    const [nonDismissable, setNonDismissable] = useState(false);
    const [showBasicOption, setShowBasicOption] = useState(false);
    const [conversionCheckout, setConversionCheckout] = useState(false);
    const skipPendingCheckoutAutoOpenRef = useRef(false);

    const openCheckout = useCallback((planId?: SubscriptionPlanId, options?: OpenCheckoutOptions) => {
        setInitialPlanId(planId);
        setMode(options?.mode ?? "checkout");
        setInitialStep(options?.initialStep ?? "plan");
        setNonDismissable(Boolean(options?.nonDismissable));
        setShowBasicOption(Boolean(options?.showBasicOption));
        setConversionCheckout(Boolean(options?.conversionCheckout));
        setOpen(true);
    }, []);

    const closeCheckout = useCallback(() => {
        setOpen(false);
        setNonDismissable(false);
        setShowBasicOption(false);
        setConversionCheckout(false);
    }, []);

    const value = useMemo(
        () => ({
            open,
            initialPlanId,
            mode,
            initialStep,
            nonDismissable,
            showBasicOption,
            conversionCheckout,
            openCheckout,
            closeCheckout,
            skipPendingCheckoutAutoOpenRef,
        }),
        [open, initialPlanId, mode, initialStep, nonDismissable, showBasicOption, conversionCheckout, openCheckout, closeCheckout, skipPendingCheckoutAutoOpenRef],
    );

    return <SubscriptionCheckoutContext.Provider value={value}>{children}</SubscriptionCheckoutContext.Provider>;
}

export function useSubscriptionCheckout() {
    const ctx = useContext(SubscriptionCheckoutContext);
    if (!ctx) {
        throw new Error("useSubscriptionCheckout must be used within SubscriptionCheckoutProvider");
    }
    return ctx;
}
